import React, {useEffect, useState} from "react";
import {apiAdminListNews, apiAdminUnpublishNews, apiAdminArchiveNews, apiAdminRestoreNews, apiAdminPublishNews, apiAdminSetFeatured, apiAdminUnsetFeatured, apiAdminDeleteNews} from "../api/news";
import AdminNewsEditor from "./AdminNewsEditor";
import NewsPreview from "./NewsPreview";
import {C} from "../styles";
import {useConfirm, useToast} from "./FeedbackProvider";

const STATUS_LABELS = {draft: "Brouillon", published: "Publiée", archived: "Archivée"};

export default function AdminNewsList({user}) {
    const confirm = useConfirm();
    const {notify} = useToast();
    const isAdmin = user?.role === "admin";
    const [articles, setArticles] = useState([]);
    const [filter, setFilter] = useState("all");
    const [editing, setEditing] = useState(undefined);
    const [previewing, setPreviewing] = useState(null);
    // Seule l'impossibilité de charger la liste reste affichée dans la page (état durable de l'écran) ; les erreurs d'action
    // (publier, archiver, supprimer…) passent par la notification centralisée (useToast), comme dans le reste de l'interface.
    const [loadError, setLoadError] = useState("");

    function load() {
        apiAdminListNews(filter === "all" ? null : filter).then(data => {
            if (!Array.isArray(data)) {
                setLoadError(data?.message || data?.error || "Impossible de charger les actualités.");
                setArticles([]);
                return;
            }
            setLoadError("");
            setArticles(data);
        });
    }

    useEffect(load, [filter]);

    async function runAction(action, id) {
        const result = await action(id);
        if (result?.error) {
            notify(result.message || result.error, "error");
            return;
        }
        load();
    }

    async function handleDelete(id) {
        if (!(await confirm("Supprimer définitivement cette actualité ?", {danger: true}))) return;
        const result = await apiAdminDeleteNews(id);
        if (result?.error) {
            notify(result.message || result.error, "error");
            return;
        }
        load();
    }

    if (editing !== undefined) {
        return (
            <AdminNewsEditor
                article={editing}
                user={user}
                onCancel={() => setEditing(undefined)}
                onSaved={() => { setEditing(undefined); load(); }}
            />
        );
    }

    return (
        <div>
            <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 8}}>
                <div>
                    {["all", "draft", "published", "archived"].map(f => (
                        <button key={f} onClick={() => setFilter(f)} style={{
                            border: `1px solid ${filter === f ? C.primary : C.border}`,
                            backgroundColor: filter === f ? C.primaryLight : "#fff",
                            borderRadius: 20, padding: "6px 12px", marginRight: 6, cursor: "pointer", fontSize: 13,
                        }}>
                            {f === "all" ? "Toutes" : STATUS_LABELS[f]}
                        </button>
                    ))}
                </div>
                <button onClick={() => setEditing(null)} style={{backgroundColor: C.primary, color: "#fff", border: "none", borderRadius: 8, padding: "10px 16px", cursor: "pointer"}}>
                    + Créer une actualité
                </button>
            </div>

            {loadError && (
                <div role="alert" style={{
                    backgroundColor: C.dangerLight, color: C.danger, border: `1px solid ${C.danger}55`,
                    borderRadius: 8, padding: "10px 14px", marginBottom: 12,
                    display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12,
                }}>
                    <span>{loadError}</span>
                </div>
            )}

            {articles.map(article => (
                <div key={article.id} style={{border: `1px solid ${C.border}`, borderRadius: 10, padding: 14, marginBottom: 10}}>
                    <div style={{display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8}}>
                        <div>
                            <strong>{article.title}</strong>
                            {article.is_featured === 1 && <span style={{marginLeft: 8, fontSize: 11, color: C.primary}}>★ À la une</span>}
                            <div style={{fontSize: 12, color: C.textMuted}}>
                                {STATUS_LABELS[article.status]} · {article.category || "Sans catégorie"}
                            </div>
                        </div>
                        <div style={{display: "flex", gap: 6, flexWrap: "wrap"}}>
                            <button onClick={() => setEditing(article)} style={actionStyle(C)}>Modifier</button>
                            <button onClick={() => setPreviewing(article)} style={actionStyle(C)}>Prévisualiser</button>
                            {isAdmin && (
                                <button onClick={() => runAction(article.is_featured === 1 ? apiAdminUnsetFeatured : apiAdminSetFeatured, article.id)} style={actionStyle(C)}>
                                    {article.is_featured === 1 ? "☆ Retirer de la une" : "★ Mettre à la une"}
                                </button>
                            )}
                            {isAdmin && article.status === "draft" && (
                                <button onClick={() => runAction(apiAdminPublishNews, article.id)} style={actionStyle(C)}>Publier</button>
                            )}
                            {isAdmin && article.status === "published" && (
                                <>
                                    <button onClick={() => runAction(apiAdminUnpublishNews, article.id)} style={actionStyle(C)}>Retirer de la publication</button>
                                    <button onClick={() => runAction(apiAdminArchiveNews, article.id)} style={actionStyle(C)}>Archiver</button>
                                </>
                            )}
                            {isAdmin && article.status === "draft" && (
                                <button onClick={() => runAction(apiAdminArchiveNews, article.id)} style={actionStyle(C)}>Archiver</button>
                            )}
                            {isAdmin && article.status === "archived" && (
                                <>
                                    <button onClick={() => runAction(apiAdminPublishNews, article.id)} style={actionStyle(C)}>Republier</button>
                                    <button onClick={() => runAction(apiAdminRestoreNews, article.id)} style={actionStyle(C)}>Restaurer en brouillon</button>
                                </>
                            )}
                            {article.status !== "published" && (
                                <button onClick={() => handleDelete(article.id)} style={{...actionStyle(C), color: C.danger, borderColor: C.danger}}>Supprimer</button>
                            )}
                        </div>
                    </div>
                </div>
            ))}
            {articles.length === 0 && <p style={{color: C.textMuted, textAlign: "center", padding: 24}}>Aucune actualité pour ce filtre.</p>}

            {previewing && <NewsPreview article={previewing} onClose={() => setPreviewing(null)}/>}
        </div>
    );
}

function actionStyle(C) {
    return {border: `1px solid ${C.border}`, borderRadius: 6, padding: "6px 10px", fontSize: 12, cursor: "pointer", backgroundColor: "#fff"};
}
